import React, { useState, useEffect } from "react";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import { auth, firestore, database } from "../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { ref, onValue, get } from "firebase/database";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Building2,
  Banknote,
} from "lucide-react";
import AccountingCashbookTab from "../admin/accounting/AccountingCashbookTab";
import { PAYMENT_TABS } from "./companyPaymentTabs";

const Payments = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [accountBalances, setAccountBalances] = useState({});
  const [farmerPayments, setFarmerPayments] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [accountTransactions, setAccountTransactions] = useState({});

  // Fetch company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setCompanyName(data.companyName);
        }
      } catch (error) {
        console.error("Error fetching company name:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyName();
  }, []);

  // Fetch account balances
  useEffect(() => {
    if (!companyName) return;

    const balancesRef = ref(
      database,
      `Companies/${companyName}/AccountBalances`
    );
    const unsubscribe = onValue(balancesRef, (snapshot) => {
      const data = snapshot.val() || {};
      setAccountBalances(data);
    });

    return () => unsubscribe();
  }, [companyName]);

  // Fetch farmer payments
  useEffect(() => {
    if (!companyName) return;

    const fetchFarmerPayments = async () => {
      try {
        const paymentsRef = ref(
          database,
          `Companies/${companyName}/FarmerPayments`
        );
        const snapshot = await get(paymentsRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const paymentsList = [];

          // Flatten the nested structure
          Object.entries(data).forEach(([farmerName, years]) => {
            if (!years || typeof years !== "object") return;
            Object.entries(years).forEach(([year, months]) => {
              if (!months || typeof months !== "object") return;
              Object.entries(months).forEach(([month, payments]) => {
                if (!payments || typeof payments !== "object") return;
                Object.entries(payments).forEach(([paymentId, payment]) => {
                  paymentsList.push({
                    id: paymentId,
                    farmerName,
                    year,
                    month,
                    ...payment,
                  });
                });
              });
            });
          });

          // Sort by date (newest first)
          paymentsList.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          setFarmerPayments(paymentsList);
        }
      } catch (error) {
        console.error("Error fetching farmer payments:", error);
      }
    };

    fetchFarmerPayments();
  }, [companyName]);

  // Fetch client payments (robust: handles nested client/year/month/paymentId and legacy flat structures)
  useEffect(() => {
    if (!companyName) return;

    const fetchClientPayments = async () => {
      try {
        const paymentsRef = ref(
          database,
          `Companies/${companyName}/NewClientPaymentRecord`
        );
        const snapshot = await get(paymentsRef);

        if (!snapshot.exists()) {
          setClientPayments([]);
          return;
        }

        const data = snapshot.val();
        const paymentsList = [];

        // Helper to normalize and push payment entries
        const pushPayment = (
          paymentId,
          clientNameFromPath,
          yearFromPath,
          monthFromPath,
          payment
        ) => {
          const timestamp =
            payment.timestamp ??
            payment.createdAt ??
            (payment.date ? Date.parse(payment.date) : null) ??
            Date.now();
          const year =
            yearFromPath ??
            payment.year ??
            (timestamp ? new Date(timestamp).getFullYear() : null);
          const month =
            monthFromPath ??
            payment.month ??
            (timestamp ? new Date(timestamp).getMonth() + 1 : null);

          paymentsList.push({
            id: paymentId,
            clientName:
              clientNameFromPath || payment.client || payment.payerName || "",
            payerName: payment.payerName ?? null,
            msisdn: payment.msisdn ?? null,
            amount: payment.amount ?? null,
            timestamp,
            date: payment.date ?? (timestamp ? new Date(timestamp).toISOString() : null),
            year,
            month,
            method: payment.method ?? payment.Mode ?? null,
            originalBillRef:
              payment.originalBillRef ?? payment.normalizedBillRef ?? null,
            transactionId: payment.transactionId ?? null,
            raw: payment,
          });
        };

        // Traverse top-level entries
        Object.entries(data).forEach(([firstKey, value]) => {
          // Case 1: nested by client -> year -> month -> paymentId
          if (
            value &&
            typeof value === "object" &&
            Object.keys(value).some((k) => /^\d{4}$/.test(k))
          ) {
            const clientName = firstKey;
            Object.entries(value).forEach(([year, months]) => {
              if (!months || typeof months !== "object") return;
              Object.entries(months).forEach(([month, payments]) => {
                if (!payments || typeof payments !== "object") return;
                Object.entries(payments).forEach(([paymentId, payment]) => {
                  pushPayment(paymentId, clientName, year, month, payment);
                });
              });
            });
          } else if (
            value &&
            typeof value === "object" &&
            (value.amount !== undefined ||
              value.timestamp !== undefined ||
              value.date !== undefined ||
              value.payerName !== undefined)
          ) {
            // Case 2: flat: paymentId -> payment
            const paymentId = firstKey;
            const payment = value;
            pushPayment(paymentId, payment.client || payment.payerName || "", payment.year, payment.month, payment);
          } else {
            // Case 3: clientName -> paymentId[] or clientName -> other nested shapes
            const clientName = firstKey;
            const maybePayments = value;
            if (maybePayments && typeof maybePayments === "object") {
              // If payments are keyed by paymentId directly
              const isPaymentIdMap = Object.values(maybePayments).some(
                (v) => v && (v.amount !== undefined || v.timestamp !== undefined)
              );
              if (isPaymentIdMap) {
                Object.entries(maybePayments).forEach(([paymentId, payment]) => {
                  pushPayment(paymentId, clientName, payment.year, payment.month, payment);
                });
              } else {
                // Try deeper traversal: clientName -> maybe year -> maybe month -> payments
                Object.entries(maybePayments).forEach(([k1, v1]) => {
                  if (v1 && typeof v1 === "object") {
                    Object.entries(v1).forEach(([k2, payments]) => {
                      if (!payments || typeof payments !== "object") return;
                      Object.entries(payments).forEach(([paymentId, payment]) => {
                        const maybeYear = /^\d{4}$/.test(k1) ? k1 : (/^\d{4}$/.test(k2) ? k2 : null);
                        const maybeMonth = maybeYear === k1 ? k2 : maybeYear === k2 ? k1 : null;
                        pushPayment(paymentId, clientName, maybeYear, maybeMonth, payment);
                      });
                    });
                  }
                });
              }
            }
          }
        });

        // Sort by timestamp (newest first)
        paymentsList.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setClientPayments(paymentsList);
      } catch (error) {
        console.error("Error fetching client payments:", error);
      }
    };

    fetchClientPayments();
  }, [companyName]);

  // Fetch account transactions for financial accounts tab
  useEffect(() => {
    if (!companyName) return;

    const fetchAccountTransactions = async () => {
      try {
        const accountsRef = ref(database, `Companies/${companyName}/Accounts`);
        const snapshot = await get(accountsRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const transactions = {};

          // Process each account type
          Object.entries(data).forEach(([accountType, years]) => {
            transactions[accountType] = [];
            if (!years || typeof years !== "object") return;
            Object.entries(years).forEach(([year, months]) => {
              if (!months || typeof months !== "object") return;
              Object.entries(months).forEach(([month, trans]) => {
                if (!trans || typeof trans !== "object") return;
                Object.entries(trans).forEach(([transId, transaction]) => {
                  transactions[accountType].push({
                    id: transId,
                    year,
                    month,
                    ...transaction,
                  });
                });
              });
            });
            // Sort by date (newest first)
            transactions[accountType].sort((a, b) =>
              new Date(b.date) - new Date(a.date)
            );
          });

          setAccountTransactions(transactions);
        }
      } catch (error) {
        console.error("Error fetching account transactions:", error);
      }
    };

    fetchAccountTransactions();
  }, [companyName]);

  // Calculate summary statistics
  const calculateSummary = () => {
    const totalFarmerPayments = farmerPayments.reduce(
      (sum, p) => sum + (p.amount || 0),
      0
    );
    const totalClientPayments = clientPayments.reduce(
      (sum, p) => sum + (p.amount || 0),
      0
    );
    const netCashFlow = totalClientPayments - totalFarmerPayments;

    // Calculate payment methods breakdown
    const paymentMethods = {};
    [...farmerPayments, ...clientPayments].forEach((p) => {
      const method = p.paymentMethod || p.Mode || p.method || "Unknown";
      paymentMethods[method] = (paymentMethods[method] || 0) + (p.amount || 0);
    });

    return {
      totalFarmerPayments,
      totalClientPayments,
      netCashFlow,
      farmerPaymentsCount: farmerPayments.length,
      clientPaymentsCount: clientPayments.length,
      paymentMethods,
    };
  };

  const summary = calculateSummary();

  // Export transactions function (overview export)
  const exportTransactions = () => {
    const allTransactions = [...farmerPayments, ...clientPayments].sort(
      (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
    );

    // Helper function to escape CSV values
    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      // If value contains comma, quote, or newline, wrap in quotes and escape quotes
      if (
        stringValue.includes(",") ||
        stringValue.includes('"') ||
        stringValue.includes("\n")
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const csvContent = [
      ["Type", "Name", "Amount", "Date", "Period", "Method", "OriginalBillRef", "PayerName"].join(","),
      ...allTransactions.map((p) =>
        [
          escapeCsvValue(p.farmerName ? "Farmer Payment" : "Client Payment"),
          escapeCsvValue(p.farmerName || p.clientName),
          p.amount || 0,
          escapeCsvValue(new Date(p.timestamp).toLocaleDateString()),
          escapeCsvValue(`${p.month ?? ""}/${p.year ?? ""}`),
          escapeCsvValue(p.paymentMethod || p.Mode || p.method || "N/A"),
          escapeCsvValue(p.originalBillRef || ""),
          escapeCsvValue(p.payerName || ""),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `all_transactions_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Loading...</p>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-cyan-700 flex items-center gap-2">
              <Wallet size={32} />
              Payments & Transactions
            </h1>
            <p className="text-gray-600 mt-2">
              Manage and track all financial transactions
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200">
          {PAYMENT_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-4 font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.id
                  ? "text-cyan-700 border-b-2 border-cyan-700"
                  : "text-gray-500 hover:text-cyan-600"
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <SummaryCard
                title="Total Farmer Payments"
                value={`KES ${summary.totalFarmerPayments.toLocaleString()}`}
                icon={<TrendingDown className="text-red-600" />}
                color="bg-red-50"
                count={`${summary.farmerPaymentsCount} payments`}
              />
              <SummaryCard
                title="Total Client Payments"
                value={`KES ${summary.totalClientPayments.toLocaleString()}`}
                icon={<TrendingUp className="text-green-600" />}
                color="bg-green-50"
                count={`${summary.clientPaymentsCount} payments`}
              />
              <SummaryCard
                title="Net Cash Flow"
                value={`KES ${summary.netCashFlow.toLocaleString()}`}
                icon={
                  <DollarSign
                    className={summary.netCashFlow >= 0 ? "text-green-600" : "text-red-600"}
                  />
                }
                color={summary.netCashFlow >= 0 ? "bg-green-50" : "bg-red-50"}
                count={summary.netCashFlow >= 0 ? "Profit" : "Loss"}
              />
              <SummaryCard
                title="Company Cash"
                value={`KES ${(accountBalances.company_cash?.balance || 0).toLocaleString()}`}
                icon={<Wallet className="text-cyan-600" />}
                color="bg-cyan-50"
                count="Available"
              />
            </div>

            {/* Payment Methods Breakdown */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CreditCard size={20} />
                Payment Methods Breakdown
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(summary.paymentMethods).map(([method, amount]) => (
                  <div key={method} className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">{method}</p>
                    <p className="text-xl font-bold text-cyan-700">
                      KES {amount.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Cash Flow Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <ArrowDownRight className="text-red-600" size={20} />
                  Farmer Payments Trend
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Paid Out</span>
                    <span className="text-lg font-semibold text-red-600">
                      KES {summary.totalFarmerPayments.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-red-500 h-3 rounded-full"
                      style={{
                        width: `${Math.min(
                          (summary.totalFarmerPayments /
                            (summary.totalFarmerPayments + summary.totalClientPayments || 1)) *
                            100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <ArrowUpRight className="text-green-600" size={20} />
                  Client Payments Trend
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Received</span>
                    <span className="text-lg font-semibold text-green-600">
                      KES {summary.totalClientPayments.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full"
                      style={{
                        width: `${Math.min(
                          (summary.totalClientPayments /
                            (summary.totalFarmerPayments + summary.totalClientPayments || 1)) *
                            100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Recent Transactions</h3>
                <button
                  onClick={() => exportTransactions()}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
                >
                  <Download size={16} />
                  Export
                </button>
              </div>
              <div className="space-y-3">
                {[...farmerPayments, ...clientPayments]
                  .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
                  .slice(0, 10)
                  .map((payment, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            payment.farmerName ? "bg-red-100" : "bg-green-100"
                          }`}
                        >
                          {payment.farmerName ? (
                            <ArrowDownRight className="text-red-600" size={20} />
                          ) : (
                            <ArrowUpRight className="text-green-600" size={20} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {payment.farmerName || payment.clientName}
                          </p>
                          <p className="text-sm text-gray-600">
                            {payment.date
                              ? new Date(payment.date).toLocaleDateString()
                              : new Date(payment.timestamp).toLocaleDateString()}{" "}
                            • {payment.paymentMethod || payment.Mode || payment.method || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`font-semibold ${
                            payment.farmerName ? "text-red-600" : "text-green-600"
                          }`}
                        >
                          {payment.farmerName ? "-" : "+"} KES{" "}
                          {(payment.amount || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {payment.farmerName ? "Payment Out" : "Payment In"}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Farmer Payments Tab */}
        {activeTab === "farmer-payments" && (
          <PaymentsTable payments={farmerPayments} type="farmer" title="Farmer Payments" />
        )}

        {/* Client Payments Tab */}
        {activeTab === "client-payments" && (
          <PaymentsTable payments={clientPayments} type="client" title="Client Payments" />
        )}

        {/* Financial Accounts Tab */}
        {activeTab === "accounts" && (
          <div className="space-y-6">
            {/* Account Balances */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AccountCard
                title="Farmer Payable"
                balance={accountBalances.farmer_payable?.balance || 0}
                description="Amount owed to farmers"
                icon={<Users className="text-orange-600" />}
                transactionCount={accountTransactions.farmer_payable?.length || 0}
              />
              <AccountCard
                title="Farmer Advance"
                balance={accountBalances.farmer_advance?.balance || 0}
                description="Advances given to farmers"
                icon={<DollarSign className="text-blue-600" />}
                transactionCount={accountTransactions.farmer_advance?.length || 0}
              />
              <AccountCard
                title="Shop Receivable"
                balance={accountBalances.shop_receivable?.balance || 0}
                description="Amount receivable from shops"
                icon={<Building2 className="text-purple-600" />}
                transactionCount={accountTransactions.shop_receivable?.length || 0}
              />
              <AccountCard
                title="Company Cash"
                balance={accountBalances.company_cash?.balance || 0}
                description="Cash on hand"
                icon={<Banknote className="text-green-600" />}
                transactionCount={accountTransactions.company_cash?.length || 0}
              />
              <AccountCard
                title="Company Bank"
                balance={accountBalances.company_bank?.balance || 0}
                description="Bank balance"
                icon={<Building2 className="text-cyan-600" />}
                transactionCount={accountTransactions.company_bank?.length || 0}
              />
              <AccountCard
                title="Liabilities"
                balance={accountBalances.liabilities?.balance || 0}
                description="Total liabilities"
                icon={<TrendingDown className="text-red-600" />}
                transactionCount={accountTransactions.liabilities?.length || 0}
              />
            </div>

            {/* Account Transactions */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Account Transaction History</h3>
                <p className="text-sm text-gray-600 mt-1">View all money movements across different accounts</p>
              </div>

              <div className="p-6">
                {Object.entries(accountTransactions).length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No transaction history available</p>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(accountTransactions).map(([accountType, transactions]) => (
                      <div key={accountType} className="border-l-4 border-cyan-500 pl-4">
                        <h4 className="font-semibold text-gray-800 mb-3 capitalize">
                          {accountType.replace(/_/g, " ")} Account
                        </h4>
                        <div className="space-y-2">
                          {transactions.slice(0, 5).map((trans, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    trans.direction === "credit" ? "bg-green-100" : "bg-red-100"
                                  }`}
                                >
                                  {trans.direction === "credit" ? (
                                    <ArrowUpRight className="text-green-600" size={16} />
                                  ) : (
                                    <ArrowDownRight className="text-red-600" size={16} />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-800">
                                    {trans.notes || trans.description || "Transaction"}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {trans.date ? new Date(trans.date).toLocaleDateString() : ""} {trans.type && `• ${trans.type}`}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`font-semibold ${trans.direction === "credit" ? "text-green-600" : "text-red-600"}`}>
                                  {trans.direction === "credit" ? "+" : "-"} KES {(trans.amount || 0).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                          {transactions.length > 5 && (
                            <p className="text-sm text-cyan-600">
                              View {transactions.length - 5} more transactions
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "cashbook" && (
          <AccountingCashbookTab recordScope={`company:${companyName || "unknown"}`} />
        )}
      </div>
    </CompanyLayout>
  );
};

// Summary Card Component
const SummaryCard = ({ title, value, icon, color, count }) => (
  <div className={`${color} rounded-lg shadow p-6`}>
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
        {icon}
      </div>
    </div>
    <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{count}</p>
  </div>
);

// Account Card Component
const AccountCard = ({ title, balance, description, icon, transactionCount }) => (
  <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
    <div className="flex items-center justify-between mb-3">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
        {icon || <Wallet className="text-gray-600" />}
      </div>
      {transactionCount > 0 && (
        <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full">
          {transactionCount} txns
        </span>
      )}
    </div>
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-3xl font-bold text-cyan-700 mb-2">
      KES {balance.toLocaleString()}
    </p>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

// Payments Table Component
const PaymentsTable = ({ payments, type, title }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");

  const filteredPayments = payments.filter((payment) => {
    const nameMatch = (payment.farmerName || payment.clientName || payment.payerName || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const monthMatch = filterMonth ? String(payment.month) === String(filterMonth) : true;
    const yearMatch = filterYear ? String(payment.year) === String(filterYear) : true;
    return nameMatch && monthMatch && yearMatch;
  });

  // Calculate statistics for filtered payments
  const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const avgAmount = filteredPayments.length > 0 ? totalAmount / filteredPayments.length : 0;

  // Populate "All Years" and "All Months" spinners
  const currentYear = new Date().getFullYear();
  let minYear = currentYear - 5;
  let maxYear = currentYear;

  if (payments.length > 0) {
    const yearsFromData = payments
      .map((p) => {
        const y = p.year !== undefined && p.year !== null ? Number(p.year) : NaN;
        if (!isNaN(y) && isFinite(y)) return y;
        return p.timestamp ? new Date(p.timestamp).getFullYear() : NaN;
      })
      .filter((y) => !isNaN(y));

    if (yearsFromData.length > 0) {
      minYear = Math.min(...yearsFromData, minYear);
      maxYear = Math.max(...yearsFromData, maxYear);
    }
  }

  const allYears = [];
  for (let y = maxYear; y >= minYear; y--) {
    allYears.push(String(y));
  }

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const allMonths = monthNames.map((name, idx) => ({
    value: String(idx + 1),
    label: name
  }));

  const exportPayments = () => {
    // Helper function to escape CSV values
    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // If value contains comma, quote, or newline, wrap in quotes and escape quotes
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const csvContent = [
      ['Name', 'Amount', 'Date', 'Period', 'Method', 'OriginalBillRef', 'PayerName'].join(','),
      ...filteredPayments.map(p => [
        escapeCsvValue(p.clientName || p.payerName || p.farmerName || ''),
        p.amount || 0,
        escapeCsvValue(p.date ? new Date(p.date).toLocaleDateString() : (p.timestamp ? new Date(p.timestamp).toLocaleDateString() : '')),
        escapeCsvValue(`${p.month ?? ''}/${p.year ?? ''}`),
        escapeCsvValue(p.method || p.paymentMethod || p.Mode || p.originalBillRef || 'N/A'),
        escapeCsvValue(p.originalBillRef || ''),
        escapeCsvValue(p.payerName || '')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_payments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <button
            onClick={exportPayments}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Total Payments</p>
            <p className="text-2xl font-bold text-gray-800">{filteredPayments.length}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Total Amount</p>
            <p className="text-2xl font-bold text-cyan-700">KES {totalAmount.toLocaleString()}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Average Amount</p>
            <p className="text-2xl font-bold text-gray-800">KES {Math.round(avgAmount).toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          >
            <option value="">All Years</option>
            {allYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          >
            <option value="">All Months</option>
            {allMonths.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {type === "farmer" ? "Farmer" : "Client"}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Period
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Method
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPayments.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  No payments found
                </td>
              </tr>
            ) : (
              filteredPayments.map((payment, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {payment.farmerName || payment.clientName || payment.payerName}
                    </div>
                    {payment.msisdn && (
                      <div className="text-xs text-gray-500">{payment.msisdn}</div>
                    )}
                    {payment.payerName && payment.payerName !== (payment.clientName) && (
                      <div className="text-xs text-gray-500">Payer: {payment.payerName}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-semibold ${type === 'farmer' ? 'text-red-600' : 'text-green-600'}`}>
                      KES {(payment.amount || 0).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {payment.date ? new Date(payment.date).toLocaleDateString() : (payment.timestamp ? new Date(payment.timestamp).toLocaleDateString() : '')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {payment.month}/{payment.year}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                        {payment.method || payment.paymentMethod || payment.Mode || payment.originalBillRef || 'N/A'}
                      </span>
                      {payment.originalBillRef && (
                        <div className="text-xs text-gray-500 mt-1">Ref: {payment.originalBillRef}</div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Payments;
